// src/components/common/ErrorBoundary.tsx
import * as React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Algo salió mal</h3>
          <p className="text-sm text-gray-500 max-w-sm mb-4">
            Ha ocurrido un error inesperado. Por favor, intenta de nuevo.
          </p>
          {this.state.error && (
            <p className="text-xs text-gray-400 font-mono mb-4 max-w-md truncate">
              {this.state.error.message}
            </p>
          )}
          <Button variant="outline" onClick={this.handleRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reintentar
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook para usar ErrorBoundary de forma declarativa
function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((err: Error) => {
    setError(err);
  }, []);

  return { error, resetError, captureError };
}

// Componente de error para queries
interface QueryErrorProps {
  error: Error | null;
  onRetry?: () => void;
}

function QueryError({ error, onRetry }: QueryErrorProps) {
  if (!error) return null;

  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="text-red-500 mb-3">
        <AlertTriangle className="h-8 w-8" />
      </div>
      <h4 className="text-base font-medium text-gray-900 mb-1">Error al cargar datos</h4>
      <p className="text-sm text-gray-500 mb-3">{error.message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Reintentar
        </Button>
      )}
    </div>
  );
}

export { ErrorBoundary, QueryError, useErrorBoundary };
